import styled, { css } from 'styled-components';
import { respondToWidth } from 'styles/general/respondTo';

/**
 * Skeletons for the branded checkout header (price row, first-month ribbon, white pricing box,
 * "Coupon applied" line). Extracted verbatim from SignUpModal so the direct-checkout links and
 * PaymentModal reserve identical space while the default-coupon check and course fetch resolve.
 */
export const PulseSkeleton = styled.div`
  @keyframes pulse {
    0%,
    100% {
      opacity: 0.3;
    }
    50% {
      opacity: 0.15;
    }
  }
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
  background-color: ${({ theme: { colors } }) => colors.neutrals[10]};
  border-radius: 8px;
`;

export const PriceSkeleton = styled(PulseSkeleton)`
  height: 42px;
  width: 250px;
  margin: 0 auto 35px auto;

  ${respondToWidth.s`
    height: 42px;
    width: 250px;
    margin-bottom: 20px; /* Real PriceLabel margin at this breakpoint. */
  `}
`;

export const InstallmentPriceSkeleton = styled(PulseSkeleton)`
  height: 42px;
  width: 337px;
  margin: 0 auto 35px auto;

  ${respondToWidth.s`
    height: 42px;
    width: 300px;
    margin-bottom: 20px; /* Real PriceLabel margin at this breakpoint. */
  `}
`;

/**
 * The white pricing box. Its real height is row-count dependent (SuccessCoupon: 12px padding ×2,
 * 20px rows, 7px grid gap), and the row count is knowable at mount — pass `$rows` so the skeleton
 * reserves the exact final height instead of trading one layout jump for another. Default 6 keeps
 * the historical SignUpModal footprint.
 */
export const CouponBoxSkeleton = styled.div<{ $rows?: number }>`
  width: 337px;
  height: ${({ $rows = 6 }) => 24 + $rows * 20 + ($rows - 1) * 7}px;
  display: flex;
  flex-direction: column;
  gap: 7px;
  margin: 0 auto 10px auto;
  border-radius: 8px;
  padding: 12px;
  background-color: ${({ theme: { colors } }) => colors.neutrals[11]};
  overflow: hidden;

  ${respondToWidth.s`
    width: 300px;
  `}
`;

/**
 * Unlike the certificate box (fixed 337px), the real lifetime box is CONTENT-sized — its widest
 * row is "<name> <level>   $<amount>". A fixed-width skeleton visibly jumps when the real box
 * lands, so this one sizes itself from an invisible replica of that row (LifetimeBoxSizerRow),
 * rendered by the caller when the name/amount are known. 263px stays as the floor.
 */
export const LifetimeCouponBoxSkeleton = styled.div`
  width: fit-content;
  min-width: 263px;
  height: 98px;
  display: flex;
  flex-direction: column;
  gap: 7px;
  margin: 0 auto 10px auto;
  border-radius: 8px;
  padding: 12px;
  background-color: ${({ theme: { colors } }) => colors.neutrals[11]};
  overflow: hidden;
`;

/**
 * Invisible width-driver for LifetimeCouponBoxSkeleton: mirrors the real row's layout
 * (CouponWrapper: flex, 40px gap, body_basic_medium_14) at zero height. Give it two children —
 * the name/level text and the price text.
 */
export const LifetimeBoxSizerRow = styled.span`
  visibility: hidden;
  height: 0;
  overflow: hidden;
  display: flex;
  gap: 40px;
  white-space: nowrap;
  ${({ theme: { typography } }) => css`
    ${typography.body_basic_medium_14}
  `};
`;

export const PricingRowSkeleton = styled(PulseSkeleton)`
  height: 16px;
  border-radius: 4px;
`;

export const CheckboxSkeletonWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  margin: 0 auto 8px auto; /* Match ribbon spacing */
  width: 337px;

  ${respondToWidth.s`
    width: 300px;
  `}
`;

export const CheckboxSquareSkeleton = styled(PulseSkeleton)`
  width: 337px;
  height: 48px; /* Match StyledCheckbox (padding 12px + 24px content) */
  border-radius: 6px;
  flex-shrink: 0;
  margin-bottom: 0;

  ${respondToWidth.s`
    width: 300px;
  `}
`;

export const CouponSkeletonContainer = styled.div`
  width: 337px;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 0 auto;

  ${respondToWidth.s`
    width: 300px;
  `}
`;

export const CouponAppliedSkeleton = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: center;
  margin: 0 auto 25px auto;
  margin-top: 10px; /* Match SuccessMessageContainer */
  width: 337px;
  height: 20px; /* Match typography line-height more closely */

  ${respondToWidth.s`
    width: 300px;
    margin: 10px auto 25px auto;
  `}
`;

export const LifetimeCouponAppliedSkeleton = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: center;
  /* The LIFETIME SuccessMessageContainer has no top margin and 35px below (the certificate
     variant is 10/25) — same 55px total, but copying the cert margins here made the "Coupon
     applied" line jump up 10px when the real content landed. */
  margin: 0 auto 35px auto;
  width: 263px;
  height: 20px; /* Match typography line-height more closely */

  ${respondToWidth.s`
    width: 263px;
    margin: 0 auto 35px auto;
  `}
`;

export const CouponAppliedIconSkeleton = styled(PulseSkeleton)`
  width: 15px;
  height: 15px;
  border-radius: 50%;
  flex-shrink: 0;
`;

export const CouponAppliedTextSkeleton = styled(PulseSkeleton)`
  height: 16px;
  width: 120px;
  border-radius: 4px;
  margin-right: 35px; /* Match SuccessMessage margin-right */
`;

export const RemoveCouponButtonSkeleton = styled(PulseSkeleton)`
  width: 100px;
  height: 16px;
  border-radius: 4px;

  ${respondToWidth.s`
    width: 90px;
  `}
`;
